import React from 'react';
import {
  DischargePatient,
  DISPOSITION_LABELS,
  PHASE_ORDER,
  PHASE_CHIP_STYLE,
  URGENCY_CHIP_STYLE,
  URGENCY_BORDER_COLOR,
} from '~/types/discharge';
import { OwnershipFilter, PatientBoardConfig, PatientCardVM, PhaseDef } from '~/components/patient-board/types';

const initialsOf = (name: string): string =>
  name.split(/\s+/).map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const formatInsuranceType = (type: string): string =>
  type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const admitMs = (admitDate: string): number => {
  const [m, d, y] = admitDate.split('/').map(Number);
  return new Date(y, m - 1, d).getTime();
};

const formatExpectedDischarge = (admitDate: string, losTargetDays: number): string => {
  const due = new Date(admitMs(admitDate) + losTargetDays * 86_400_000);
  return due.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
};

// Coordinate's phases drive both the accordion and the board columns. The
// 'discharge' phase is non-interactive (no expandable content) — it's the
// hand-off point into the post-discharge 'followup' phase.
export const DISCHARGE_PHASES: PhaseDef[] = PHASE_ORDER.map(key => ({
  key,
  label: PHASE_CHIP_STYLE[key].label,
  bgcolor: PHASE_CHIP_STYLE[key].bgcolor,
  color: PHASE_CHIP_STYLE[key].color,
  interactive: key !== 'discharge',
}));

export const mapDischargePatient = (p: DischargePatient): PatientCardVM => ({
  id: p.id,
  name: p.name,
  age: p.age,
  dob: p.dob,
  mrn: p.mrn,
  scoreCell: { label: DISPOSITION_LABELS[p.disposition], score: p.dispositionScore },
  locationPrimary: `Floor ${p.floor} · Rm ${p.room}`,
  locationSecondary: p.facilityName,
  physician: p.attendingPhysician,
  diagnosis: p.primaryDiagnosis,
  expectedDischarge: formatExpectedDischarge(p.admitDate, p.losTargetDays),
  expectedDischargeMs: admitMs(p.admitDate) + p.losTargetDays * 86_400_000,
  currentLOS: p.currentLOS,
  phaseKey: p.phase,
  urgency: {
    label: URGENCY_CHIP_STYLE[p.urgency].label,
    bgcolor: URGENCY_CHIP_STYLE[p.urgency].bgcolor,
    color: URGENCY_CHIP_STYLE[p.urgency].color,
    borderColor: URGENCY_BORDER_COLOR[p.urgency],
  },
  assignedToName: p.assignedTo,
  assignedToInitials: p.assignedTo ? initialsOf(p.assignedTo) : null,
  headerInfoLines: [
    React.createElement(React.Fragment, null,
      `DOB ${p.dob} · Age ${p.age} · `,
      React.createElement('strong', null, `${p.facilityName} Floor ${p.floor} · Rm ${p.room}`),
    ),
    `Expected discharge ${formatExpectedDischarge(p.admitDate, p.losTargetDays)} · Admit ${p.admitDate} · ${formatInsuranceType(p.insuranceType)} · ${p.memberId}`,
    React.createElement(React.Fragment, null,
      React.createElement('strong', null, 'Primary contact:'), ' ',
      React.createElement('strong', null, p.primaryContact.name),
      ` · ${p.primaryContact.relationship} · ${p.primaryContact.phone}`,
    ),
  ],
  admitDate: p.admitDate,
  losTargetDays: p.losTargetDays,
  insuranceLine: `${p.insurance} · ${p.memberId}`,
  attending: p.attendingPhysician,
});

const titleFor = (ownership: OwnershipFilter): string =>
  ownership === 'MY' ? 'My Patients' :
  ownership === 'UNASSIGNED' ? 'Unassigned Patients' :
  'All Patients';

export const makeDischargeConfig = (onTake: (id: string) => void): PatientBoardConfig => ({
  routeBase: '/discharge',
  title: titleFor,
  phases: DISCHARGE_PHASES,
  showUrgency: true,
  scoreColumnLabel: 'Predicted Disposition',
  onTake,
  searchText: (vm) => [vm.name, vm.mrn, vm.locationSecondary, vm.physician, vm.diagnosis].join(' '),
});
